/**
 * Linear Regression Intercept Calculator
 *
 * Calculates the y-intercept of a linear regression line fitted through
 * the last N periods using least squares method.
 *
 * Formula:
 * - Slope (b) = (N * Σ(w*y) - Σw * Σy) / (N * Σ(w²) - (Σw)²)
 * - Intercept (a) = (Σy - b * Σw) / N
 *
 * Based on ChartIQ implementation (calcsChartIQ.js:495-543)
 */
export default function () {
  function calculator(data) {
    const { windowSize, sourcePath } = options;

    if (!data || data.length < windowSize) {
      return new Array(data.length).fill(undefined);
    }

    const sumWeights = (windowSize * (windowSize + 1)) / 2;
    const squaredSumWeights = Math.pow(sumWeights, 2);
    const sumWeightsSquared = (sumWeights * (2 * windowSize + 1)) / 3;

    let sumCloses = 0;
    let sumWeightedCloses = 0;
    let sumClosesSquared = 0;

    const results = [];

    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const currentValue = sourcePath ? d[sourcePath] : d.close;

      if (currentValue == null) {
        results.push(undefined);
        continue;
      }

      sumWeightedCloses += windowSize * currentValue - sumCloses;
      sumCloses += currentValue;
      sumClosesSquared += Math.pow(currentValue, 2);

      if (i < windowSize - 1) {
        results.push(undefined);
        continue;
      }

      if (i > windowSize - 1) {
        const oldValue = sourcePath
          ? data[i - windowSize][sourcePath]
          : data[i - windowSize].close;

        if (oldValue != null) {
          sumCloses -= oldValue;
          sumClosesSquared -= Math.pow(oldValue, 2);
        }
      }

      const slope =
        (windowSize * sumWeightedCloses - sumWeights * sumCloses) /
        (windowSize * sumWeightsSquared - squaredSumWeights);

      const intercept = (sumCloses - slope * sumWeights) / windowSize;

      results.push(intercept);
    }

    return results;
  }

  let options = {
    windowSize: 14,
    sourcePath: "close",
  };

  calculator.undefinedLength = function () {
    return options.windowSize - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) return options;
    options = { ...options, ...x };
    return calculator;
  };

  return calculator;
}
